/**
 * Walk nested kalkyle trees (parent_calculation_id + item.nested_calculation)
 * so offer UX can surface anbefalte felter / struktur across all depths.
 */

import { fetchOfferCalculation } from "../../../../api/offer-calculation";
import {
  recommendedFieldsNeedAttention,
  recommendedInsertGroups,
} from "../calculations/nested-module-insert-fields-core";

function calcId(calc) {
  return calc?.id != null ? String(calc.id) : null;
}

/**
 * Direct children: association + item-linked nested calcs (deduped).
 * Prefer item row_order (line hierarchy) so «Tilpass mal» walks top → down.
 */
export function childCalculationsOf(calc) {
  const seen = new Set();
  const ordered = [];

  const push = (child) => {
    if (!child) return;
    const id = calcId(child);
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    } else {
      const localKey = `local:${ordered.length}`;
      if (seen.has(localKey)) return;
      seen.add(localKey);
    }
    ordered.push(child);
  };

  const items = Array.isArray(calc?.items) ? [...calc.items] : [];
  items
    .sort(
      (a, b) =>
        (Number(a?.row_order) || 0) - (Number(b?.row_order) || 0) ||
        (Number(a?.id) || 0) - (Number(b?.id) || 0)
    )
    .forEach((item) => {
      if (item?.nested_calculation) {
        push(item.nested_calculation);
        return;
      }
      if (item?.nested_calculation_id != null) {
        push({ id: item.nested_calculation_id });
      }
    });

  (Array.isArray(calc?.nested_calculations) ? calc.nested_calculations : []).forEach(
    push
  );

  return ordered;
}

/**
 * Depth-first walk. Visitor receives { calc, depth, pathTitles, parent }.
 * Skips duplicate ids (diamond / dual-link via association + item).
 */
export function walkNestedCalculationTree(root, visitor) {
  if (!root) return;
  const seen = new Set();

  const visit = (calc, depth, pathTitles, parent) => {
    if (!calc) return;
    const id = calcId(calc);
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    }
    const titles = [...pathTitles, calc.title || (id ? `#${id}` : "")].filter(
      Boolean
    );
    visitor({ calc, depth, pathTitles: titles, parent });
    childCalculationsOf(calc).forEach((child) =>
      visit(child, depth + 1, titles, calc)
    );
  };

  visit(root, 0, [], null);
}

/** Flatten tree nodes (root first). */
export function flattenNestedCalculationTree(root) {
  const rows = [];
  walkNestedCalculationTree(root, (entry) => {
    rows.push(entry);
  });
  return rows;
}

/**
 * Recommended insert groups for every calc in the tree (incl. root).
 * @returns {Array<{ calc, depth, pathTitles, groups, needsAttention }>}
 */
export function collectRecommendedInsertTree(root, t) {
  const out = [];
  walkNestedCalculationTree(root, ({ calc, depth, pathTitles }) => {
    const groups = recommendedInsertGroups(calc?.module_graph, t);
    if (!groups.length && !recommendedFieldsNeedAttention(calc?.module_graph, t)) {
      // Still list modules with empty unlocked sets only when they look modular
      if (!(calc?.module_enabled || (calc?.module_graph?.nodes || []).length)) {
        return;
      }
    }
    if (!groups.length) return;
    out.push({
      calc,
      depth,
      pathTitles,
      groups,
      needsAttention: recommendedFieldsNeedAttention(calc?.module_graph, t),
    });
  });
  return out;
}

export function nestedTreeNeedsAttention(root, t) {
  return collectRecommendedInsertTree(root, t).some((row) => row.needsAttention);
}

export function nestedTreeRecommendedGroupCount(root, t) {
  return collectRecommendedInsertTree(root, t).reduce(
    (sum, row) => sum + row.groups.length,
    0
  );
}

function needsHydrate(calc) {
  if (!calc?.id) return true;
  const hasGraph =
    Array.isArray(calc?.module_graph?.nodes) && calc.module_graph.nodes.length > 0;
  const knowsModule = calc.module_enabled != null;
  const hasNestedList = Array.isArray(calc.nested_calculations);
  const hasItems = Array.isArray(calc.items);
  // Incomplete embed from parent item as_json — refetch for tree + fields.
  if (!knowsModule && !hasGraph) return true;
  if (!hasNestedList || !hasItems) return true;
  return false;
}

/**
 * Fetch missing nodes so recommended fields / structure walk the full tree.
 * Caps recursion to avoid runaway loops.
 */
export async function hydrateNestedCalculationTree(root, { maxFetches = 40 } = {}) {
  if (!root) return null;
  let fetches = 0;
  const cache = new Map();

  const load = async (seed) => {
    if (!seed) return null;
    const id = calcId(seed);
    if (id && cache.has(id)) return cache.get(id);

    let calc = seed;
    if (id && needsHydrate(seed) && fetches < maxFetches) {
      fetches += 1;
      const full = await fetchOfferCalculation(Number(id) || id);
      if (full && !full.errors) {
        calc = {
          ...seed,
          ...full,
          divide_by_quantity: seed.divide_by_quantity,
        };
      }
    }
    if (id) cache.set(id, calc);

    const nestedList = Array.isArray(calc.nested_calculations)
      ? calc.nested_calculations
      : [];
    const nextNested = [];
    for (const child of nestedList) {
      nextNested.push(await load(child));
    }

    const items = Array.isArray(calc.items) ? calc.items : [];
    const nextItems = [];
    for (const item of items) {
      if (!item?.nested_calculation && item?.nested_calculation_id == null) {
        nextItems.push(item);
        continue;
      }
      const linked =
        item.nested_calculation ||
        (item.nested_calculation_id != null
          ? { id: item.nested_calculation_id }
          : null);
      const hydratedChild = await load(linked);
      nextItems.push({
        ...item,
        nested_calculation_id:
          hydratedChild?.id ?? item.nested_calculation_id,
        nested_calculation: hydratedChild
          ? {
              ...(item.nested_calculation || {}),
              ...hydratedChild,
              divide_by_quantity:
                item.nested_calculation?.divide_by_quantity ??
                hydratedChild.divide_by_quantity,
            }
          : item.nested_calculation,
      });
    }

    const merged = {
      ...calc,
      nested_calculations: nextNested.filter(Boolean),
      items: nextItems,
    };
    if (id) cache.set(id, merged);
    return merged;
  };

  return load(root);
}

/** Patch a calc somewhere in the tree by id (immutable). */
export function mapCalculationInTree(root, targetId, mapper) {
  if (!root || targetId == null) return root;
  const tid = String(targetId);

  const mapCalc = (calc) => {
    if (!calc) return calc;
    const self =
      calcId(calc) === tid ? mapper(calc) || calc : calc;
    return {
      ...self,
      nested_calculations: (Array.isArray(self.nested_calculations)
        ? self.nested_calculations
        : []
      ).map(mapCalc),
      items: (Array.isArray(self.items) ? self.items : []).map((item) => {
        if (
          item?.nested_calculation_id == null &&
          !item?.nested_calculation
        ) {
          return item;
        }
        const nested = item.nested_calculation
          ? mapCalc(item.nested_calculation)
          : item.nested_calculation_id != null &&
              String(item.nested_calculation_id) === tid
            ? mapCalc({ id: item.nested_calculation_id })
            : item.nested_calculation;
        return { ...item, nested_calculation: nested };
      }),
    };
  };

  return mapCalc(root);
}
